import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import { Eye, EyeOff } from 'lucide-react'
import { useAdminAuth } from "@/context/Admin"
import Loader from "@/components/loader/loader"

export default function PaymentSettingsTab() {
  const {
    getPaymentGatewayStatus,
    enablePaymentGateway,
    disablePaymentGateway,
    getTaxRate,
    updateTaxRate,
  } = useAdminAuth();

  const [loading, setLoading] = useState(true);
  const [flutterwaveEnabled, setFlutterwaveEnabled] = useState(false)
  const [flutterwavePublicKey, setFlutterwavePublicKey] = useState("")
  const [flutterwaveSecretKey, setFlutterwaveSecretKey] = useState("")
  const [showFlutterwaveSecret, setShowFlutterwaveSecret] = useState(false)
  const [updatingFlutterwave, setUpdatingFlutterwave] = useState(false)

  const [paystackEnabled, setPaystackEnabled] = useState(false)
  const [paystackPublicKey, setPaystackPublicKey] = useState("")
  const [paystackSecretKey, setPaystackSecretKey] = useState("")
  const [showPaystackSecret, setShowPaystackSecret] = useState(false)
  const [updatingPaystack, setUpdatingPaystack] = useState(false)

  const [taxRate, setTaxRate] = useState(0)
  const [newTaxRate, setNewTaxRate] = useState("")
  const [updatingTax, setUpdatingTax] = useState(false)

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const [flutterwaveStatus, paystackStatus, currentTaxRate] = await Promise.all([
          getPaymentGatewayStatus("flutterwave"),
          getPaymentGatewayStatus("paystack"),
          getTaxRate(),
        ]);
        setFlutterwaveEnabled(flutterwaveStatus);
        setPaystackEnabled(paystackStatus);
        setTaxRate(currentTaxRate || 0);
        setNewTaxRate(String(currentTaxRate || 0));
      } catch (error) {
        console.error("Error fetching payment settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [getPaymentGatewayStatus, getTaxRate]);

  const handleFlutterwaveToggle = async (enabled) => {
    setUpdatingFlutterwave(true);
    try {
      if (enabled) {
        await enablePaymentGateway("flutterwave");
        setFlutterwaveEnabled(true);
      } else {
        await disablePaymentGateway("flutterwave");
        setFlutterwaveEnabled(false);
      }
    } catch (error) {
      console.error("Error toggling Flutterwave:", error);
    } finally {
      setUpdatingFlutterwave(false);
    }
  };

  const handlePaystackToggle = async (enabled) => {
    setUpdatingPaystack(true);
    try {
      if (enabled) {
        await enablePaymentGateway("paystack");
        setPaystackEnabled(true);
      } else {
        await disablePaymentGateway("paystack");
        setPaystackEnabled(false);
      }
    } catch (error) {
      console.error("Error toggling Paystack:", error);
    } finally {
      setUpdatingPaystack(false);
    }
  };

  const handleSaveTaxRate = async () => {
    const rate = parseFloat(newTaxRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      alert("Tax rate must be a number between 0 and 100");
      return;
    }

    setUpdatingTax(true);
    try {
      await updateTaxRate(rate);
      setTaxRate(rate);
    } catch (error) {
      console.error("Error updating tax rate:", error);
      setNewTaxRate(String(taxRate));
    } finally {
      setUpdatingTax(false);
    }
  };

  const handleSaveFlutterwave = () => {
    // Note: API keys should be stored in environment variables on the backend
    // This is just a placeholder for future implementation
    // console.log("Saving Flutterwave settings:", { flutterwaveEnabled, flutterwavePublicKey, flutterwaveSecretKey })
    alert("Note: API keys should be configured via environment variables on the server.")
  }

  const handleSavePaystack = () => {
    // Note: API keys should be stored in environment variables on the backend
    // This is just a placeholder for future implementation
    // console.log("Saving Paystack settings:", { paystackEnabled, paystackPublicKey, paystackSecretKey })
    alert("Note: API keys should be configured via environment variables on the server.")
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">Flutterwave Settings</CardTitle>
          <CardDescription>Configure your Flutterwave payment gateway.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-primary">Enable Flutterwave</p>
              <p className="text-sm text-gray-600">
                Allow customers to pay using Flutterwave.
              </p>
            </div>
            <Switch 
              checked={flutterwaveEnabled} 
              onCheckedChange={handleFlutterwaveToggle}
              disabled={updatingFlutterwave}
            />
          </div>
          <Separator />
          <div>
            <Label htmlFor="flutterwavePublicKey">Public Key</Label>
            <Input
              id="flutterwavePublicKey"
              value={flutterwavePublicKey}
              onChange={(e) => setFlutterwavePublicKey(e.target.value)}
              placeholder="Enter Flutterwave public key"
              className="mt-1"
              disabled={!flutterwaveEnabled}
            />
          </div>
          <div>
            <Label htmlFor="flutterwaveSecretKey">Secret Key</Label>
            <div className="relative mt-1">
              <Input
                id="flutterwaveSecretKey"
                type={showFlutterwaveSecret ? "text" : "password"}
                value={flutterwaveSecretKey}
                onChange={(e) => setFlutterwaveSecretKey(e.target.value)}
                placeholder="Enter Flutterwave secret key"
                className="pr-10"
                disabled={!flutterwaveEnabled}
              />
              <button
                type="button"
                onClick={() => setShowFlutterwaveSecret(!showFlutterwaveSecret)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={!flutterwaveEnabled}
              >
                {showFlutterwaveSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <Button
            onClick={handleSaveFlutterwave}
            className="bg-primary hover:bg-primary/90 text-white"
            disabled={!flutterwaveEnabled}
          >
            Save Flutterwave Settings
          </Button>
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-primary">Paystack Settings</CardTitle>
          <CardDescription>Configure your Paystack payment gateway.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-primary">Enable Paystack</p>
              <p className="text-sm text-gray-600">
                Allow customers to pay using Paystack.
              </p>
            </div>
            <Switch 
              checked={paystackEnabled} 
              onCheckedChange={handlePaystackToggle}
              disabled={updatingPaystack}
            />
          </div>
          <Separator />
          <div>
            <Label htmlFor="paystackPublicKey">Public Key</Label>
            <Input
              id="paystackPublicKey"
              value={paystackPublicKey}
              onChange={(e) => setPaystackPublicKey(e.target.value)}
              placeholder="Enter Paystack public key"
              className="mt-1"
              disabled={!paystackEnabled}
            />
          </div>
          <div>
            <Label htmlFor="paystackSecretKey">Secret Key</Label>
            <div className="relative mt-1">
              <Input
                id="paystackSecretKey"
                type={showPaystackSecret ? "text" : "password"}
                value={paystackSecretKey}
                onChange={(e) => setPaystackSecretKey(e.target.value)}
                placeholder="Enter Paystack secret key"
                className="pr-10"
                disabled={!paystackEnabled}
              />
              <button
                type="button"
                onClick={() => setShowPaystackSecret(!showPaystackSecret)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={!paystackEnabled}
              >
                {showPaystackSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <Button
            onClick={handleSavePaystack}
            className="bg-primary hover:bg-primary/90 text-white"
            disabled={!paystackEnabled}
          >
            Save Paystack Settings
          </Button>
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-primary">Tax Rate</CardTitle>
          <CardDescription>Configure the default tax rate for all orders.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="taxRate">Tax Rate (%)</Label>
            <Input
              id="taxRate"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={newTaxRate}
              onChange={(e) => setNewTaxRate(e.target.value)}
              placeholder="Enter tax rate (e.g., 7.5 for 7.5%)"
              className="mt-1"
            />
            <p className="text-sm text-gray-600 mt-1">
              Current tax rate: {taxRate}%
            </p>
          </div>
          <Button
            onClick={handleSaveTaxRate}
            className="bg-primary hover:bg-primary/90 text-white"
            disabled={updatingTax || newTaxRate === String(taxRate)}
          >
            {updatingTax ? "Updating..." : "Update Tax Rate"}
          </Button>
        </CardContent>
      </Card>
    </>
  )
}
